import React, { useState, useEffect } from 'react';
import { 
  freezeMembership, 
  resumeFrozenMembership, 
  handleNoShowMonth,
  generatePaymentReminders 
} from '../firebase/paymentManager';
import './MembershipManagementPanel.css';

function MembershipManagementPanel({ tenantId, member, onClose, onSuccess }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Freeze membership state
  const [freezeData, setFreezeData] = useState({
    startDate: '',
    endDate: '',
    reason: 'medical',
    notes: ''
  });

  // No-show handling state
  const [noShowData, setNoShowData] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
    action: 'charge_full',
    notes: ''
  });

  const [reminders, setReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false); // eslint-disable-line no-unused-vars

  useEffect(() => {
    if (member) {
      loadPaymentReminders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]); // loadPaymentReminders intentionally excluded to prevent infinite loops

  const loadPaymentReminders = async () => {
    if (!tenantId || !member?.id) return;
    setLoadingReminders(true);
    try {
      const result = await generatePaymentReminders(tenantId);
      if (result.success) {
        // Filter reminders for this specific member
        const memberReminders = result.reminders.filter(r => r.memberId === member.id);
        setReminders(memberReminders);
      }
    } catch (error) {
      console.error('Error loading payment reminders:', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleFreezeMembership = async () => {
    if (!freezeData.startDate || !freezeData.endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(freezeData.startDate) >= new Date(freezeData.endDate)) {
      setError('End date must be after start date');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const result = await freezeMembership(tenantId, member.id, freezeData);
      
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to freeze membership');
      }
    } catch (error) {
      console.error('Error freezing membership:', error);
      setError(`An error occurred: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResumeMembership = async () => {
    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const result = await resumeFrozenMembership(tenantId, member.id);
      
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to resume membership');
      }
    } catch (error) {
      console.error('Error resuming membership:', error);
      setError(`An error occurred: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNoShowAction = async () => {
    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const result = await handleNoShowMonth(
        tenantId,
        member.id, 
        noShowData.month, 
        noShowData.year, 
        noShowData.action
      );
      
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to handle no-show month');
      }
    } catch (error) {
      console.error('Error handling no-show month:', error);
      setError(`An error occurred: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const getMembershipStatusColor = () => {
    switch (member.membershipStatus) {
      case 'active': return '#38a169';
      case 'suspended': return '#e53e3e';
      case 'frozen': return '#d69e2e';
      case 'terminated': return '#718096';
      default: return '#718096';
    }
  };

  const getAttendanceStatusColor = () => {
    switch (member.attendanceStatus) {
      case 'active': return '#38a169';
      case 'inactive': return '#e53e3e';
      case 'frozen': return '#d69e2e';
      default: return '#718096';
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  const renderOverviewTab = () => (
    <div className="tab-content">
      <div className="status-overview">
        <h3>Membership Status Overview</h3>
        
        <div className="status-grid">
          <div className="status-item">
            <span className="label">Membership Status:</span>
            <span 
              className="status-badge" 
              style={{ backgroundColor: getMembershipStatusColor() }}
            >
              {member.membershipStatus || 'active'}
            </span>
          </div>
          
          <div className="status-item">
            <span className="label">Attendance Status:</span>
            <span 
              className="status-badge" 
              style={{ backgroundColor: getAttendanceStatusColor() }}
            >
              {member.attendanceStatus || 'active'}
            </span>
          </div>
          
          <div className="status-item">
            <span className="label">Current Balance:</span>
            <span className={`value ${(member.currentBalance || 0) > 0 ? 'balance-due' : 'no-balance'}`}>
              {formatCurrency(member.currentBalance || 0)}
            </span>
          </div>
          
          <div className="status-item">
            <span className="label">Monthly Fee:</span>
            <span className="value">{formatCurrency(member.fees)}</span>
          </div>
        </div>

        {member.freezeHistory && member.freezeHistory.length > 0 && (
          <div className="freeze-history">
            <h4>Freeze History</h4>
            <div className="freeze-list">
              {member.freezeHistory.map((freeze, index) => (
                <div key={index} className="freeze-item">
                  <div className="freeze-dates">
                    {new Date(freeze.startDate).toLocaleDateString()} - {new Date(freeze.endDate).toLocaleDateString()}
                  </div>
                  <div className="freeze-reason">{freeze.reason}</div>
                  <div className="freeze-status">{freeze.status}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {reminders.length > 0 && (
        <div className="payment-reminders">
          <h4>Payment Reminders</h4>
          <div className="reminders-list">
            {reminders.map((reminder, index) => (
              <div key={index} className={`reminder-item ${reminder.type}`}>
                <div className="reminder-type">{reminder.type === 'overdue' ? '⚠️ Overdue' : '📅 Due Soon'}</div>
                <div className="reminder-details">
                  {reminder.type === 'overdue' 
                    ? `${reminder.daysOverdue} days overdue`
                    : `Due in ${reminder.daysUntilDue} days`
                  }
                </div>
                <div className="reminder-amount">{formatCurrency(reminder.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderFreezeTab = () => (
    <div className="tab-content">
      <div className="freeze-section">
        <h3>Freeze Membership</h3>
        <p className="section-description">
          Freeze a member's membership temporarily. This will adjust their billing cycle 
          and prevent charges during the freeze period.
        </p>

        <div className="form-group">
          <label htmlFor="freezeReason">Freeze Reason</label>
          <select
            id="freezeReason"
            value={freezeData.reason}
            onChange={(e) => setFreezeData(prev => ({ ...prev, reason: e.target.value }))}
          >
            <option value="medical">Medical/Health</option>
            <option value="travel">Travel/Relocation</option>
            <option value="personal">Personal Reasons</option>
            <option value="financial">Financial Hardship</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="freezeStartDate">Start Date</label>
            <input
              type="date"
              id="freezeStartDate"
              value={freezeData.startDate}
              onChange={(e) => setFreezeData(prev => ({ ...prev, startDate: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="freezeEndDate">End Date</label>
            <input
              type="date"
              id="freezeEndDate"
              value={freezeData.endDate}
              onChange={(e) => setFreezeData(prev => ({ ...prev, endDate: e.target.value }))}
              min={freezeData.startDate || new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="freezeNotes">Additional Notes</label>
          <textarea
            id="freezeNotes"
            value={freezeData.notes}
            onChange={(e) => setFreezeData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add any additional notes about the freeze..."
            rows="3"
          />
        </div>

        <div className="action-buttons">
          <button
            className="freeze-btn"
            onClick={handleFreezeMembership}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Freeze Membership'}
          </button>
          
          {member.attendanceStatus === 'frozen' && (
            <button
              className="resume-btn"
              onClick={handleResumeMembership}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Resume Membership'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderNoShowTab = () => (
    <div className="tab-content">
      <div className="no-show-section">
        <h3>Handle No-Show Month</h3>
        <p className="section-description">
          Manage scenarios where a member doesn't attend the gym for an entire month. 
          Choose how to handle the billing for that period.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="noShowMonth">Month</label>
            <select
              id="noShowMonth"
              value={noShowData.month}
              onChange={(e) => setNoShowData(prev => ({ ...prev, month: parseInt(e.target.value) }))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>{getMonthName(i)}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="noShowYear">Year</label>
            <select
              id="noShowYear"
              value={noShowData.year}
              onChange={(e) => setNoShowData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>{year}</option>
                );
              })}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="noShowAction">Action</label>
          <select
            id="noShowAction"
            value={noShowData.action}
            onChange={(e) => setNoShowData(prev => ({ ...prev, action: e.target.value }))}
          >
            <option value="charge_full">Charge Full Month Fee</option>
            <option value="charge_half">Charge Half Month Fee</option>
            <option value="waive">Waive Month Fee</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="noShowNotes">Notes</label>
          <textarea
            id="noShowNotes"
            value={noShowData.notes}
            onChange={(e) => setNoShowData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add notes about why this action was taken..."
            rows="3"
          />
        </div>

        <div className="action-buttons">
          <button
            className="no-show-btn"
            onClick={handleNoShowAction}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Apply Action'}
          </button>
        </div>

        <div className="no-show-info">
          <h4>No-Show Policy Guidelines</h4>
          <ul>
            <li><strong>Charge Full Month:</strong> Use when member has no valid reason for absence</li>
            <li><strong>Charge Half Month:</strong> Use for partial absences or valid partial excuses</li>
            <li><strong>Waive Month:</strong> Use for documented medical issues, travel, or special circumstances</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="tab-content">
      <div className="settings-section">
        <h3>Membership Settings</h3>
        
        <div className="setting-item">
          <label>Auto-Charge Late Fees</label>
          <select defaultValue="enabled">
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
          <small>Automatically add late fees for overdue payments</small>
        </div>

        <div className="setting-item">
          <label>Grace Period (Days)</label>
          <input type="number" defaultValue="7" min="0" max="30" />
          <small>Number of days after due date before late fees apply</small>
        </div>

        <div className="setting-item">
          <label>Late Fee Amount</label>
          <input type="number" defaultValue="100" min="0" step="10" />
          <small>Fixed late fee amount in ₹</small>
        </div>

        <div className="setting-item">
          <label>Auto-Suspend After (Days)</label>
          <input type="number" defaultValue="30" min="0" max="90" />
          <small>Automatically suspend membership after this many days overdue</small>
        </div>

        <div className="setting-item">
          <label>Attendance Tracking</label>
          <select defaultValue="enabled">
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
          <small>Track member attendance for no-show detection</small>
        </div>

        <button className="save-settings-btn">Save Settings</button>
      </div>
    </div>
  );

  return (
    <div className="membership-management-overlay" onClick={onClose}>
      <div className="membership-management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Membership Management - {member?.fullName}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`tab-btn ${activeTab === 'freeze' ? 'active' : ''}`}
              onClick={() => setActiveTab('freeze')}
            >
              Freeze/Resume
            </button>
            <button
              className={`tab-btn ${activeTab === 'no-show' ? 'active' : ''}`}
              onClick={() => setActiveTab('no-show')}
            >
              No-Show Handling
            </button>
            <button
              className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'freeze' && renderFreezeTab()}
          {activeTab === 'no-show' && renderNoShowTab()}
          {activeTab === 'settings' && renderSettingsTab()}

          {/* Error and Success Messages */}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </div>
      </div>
    </div>
  );
}

export default MembershipManagementPanel;




